import { useEffect, useMemo, useRef, useState } from "react";
import {
  CARD_NAMES,
  GET_DATA_CONTROL_CARD_SUMMARY,
  GET_FILE_AUDIT_TRAIL,
  GET_USER_AUDIT_TRAILS,
  GET_DOMAIN_AUDIT_TRAILS,
  GET_USER_LOCATION_AUDIT_TRAILS,
  GET_RESTRICTIONS_APPLIED_COUNT,
  GET_DATA_CONTROL_CARD_SUMMARY_COUNT,
} from "../DataControlConstans";
import { useDispatch, useSelector } from "react-redux";
import { getDataSourceIcon } from "../../SysGuard/Dashboard/ContentPages/SysGuardColumnConfig";

export function CarouselCard({
  category,
  cardData,
  onOpen,
  onOpenAction,
  isCenter,
  isStack = false,
  isNear = false,
}) {
  if (!cardData) {
    return <div className="fsc-risk-card">Loading...</div>;
  }

  const { summary = {}, insight = {}, action } = cardData;

  const {
    risklevel,
    datasources,
    lastdetected,
    affectedusers,
    affectedroles,
    affectedfiles,
    affectedscopes,
    alertsexposed,
    countriesexposed,
    countries_count,
  } = summary;

  const sourceList = (datasources || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  const severityMap = {
    HIGH: "red",
    MEDIUM: "amber",
    LOW: "green",
    UNKNOWN: "blue",
  };

  const severity = severityMap[risklevel] || "blue";

  const getAffected = () => {
    switch (category.id) {
      case "public-sensitive":
        return {
          label: "Affected files",
          value: affectedfiles ?? 0,
        };

      case "user-risk":
        return {
          label: "Affected users",
          value: affectedusers ?? 0,
        };

      case "org-wide":
        return {
          label: "Affected scopes",
          value: affectedscopes ?? 0,
        };

      case "location":
        return {
          label: "Affected users",
          value: affectedusers ?? 0,
        };

      case "role":
        return {
          label: "Affected roles",
          value: affectedroles ?? 0,
        };

      default:
        return {
          label: "Affected",
          value: 0,
        };
    }
  };

  const affected = getAffected();

  const getMetricValue = () => {
    switch (category.id) {
      case "public-sensitive":
        return affectedfiles ?? 0;
      case "user-risk":
        return affectedusers ?? 0;
      case "org-wide":
        return affectedscopes ?? 0;
      case "location":
        return countriesexposed ?? 0;

      default:
        return alertsexposed ?? 0;
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString();
  };

  const proximityClass = isCenter
    ? "fsc-risk-card--center"
    : isNear
      ? "fsc-risk-card--dim fsc-risk-card--near"
      : "fsc-risk-card--dim";

  const isPeek = isStack && !isCenter;

  const handleCardClick = () => {
    if (isStack || !isCenter) return;
    onOpen(category.id);
  };

  const getAction = () => {
    if (cardData.cardName === "Restrict Files of a User") {
      return "Remove Public Link";
    } else if (cardData.cardName === "Restrict Organization Files") {
      return "Remove Org-wide Public Links";
    } else if (cardData.cardName === "Restrict User — Unusual Location") {
      return "Change Status";
    } else {
      return action;
    }
  };

  return (
    <div
      className={`fsc-risk-card fsc-risk-card--${severity} ${proximityClass}`}
      style={{
        backgroundImage: `linear-gradient(
          180deg,
          rgba(255,255,255,0.93),
          rgba(255,255,255,0.97)
        ), url(${category.image})`,
      }}
      onClick={handleCardClick}
    >
      <span className="fsc-risk-card__topbar" />

      {isPeek ? (
        <div className="fsc-risk-card__peek">
          <span
            className={`fsc-risk-card__peek-icon fsc-risk-card__peek-icon--${severity}`}
          >
            <i className={`ti ${category.icon}`} />
          </span>
          <span className="fsc-risk-card__peek-name">{cardData.cardName}</span>
        </div>
      ) : (
        <>
          <div className="fsc-risk-card__head">
            <span
              className={`fsc-risk-card__icon fsc-risk-card__icon--${severity}`}
            >
              <i className={`ti ${category.icon}`} />
            </span>

            <h3 className="fsc-risk-card__title">
              {cardData.cardName === "Restrict Files of a User"
                ? "Restrict User's Access"
                : cardData.cardName === "Restrict User — Unusual Location"
                  ? "Restrict User — Unusual Behaviour"
                  : cardData.cardName}
            </h3>
          </div>
        </>
      )}

      <p className="fsc-risk-card__metric">
        <span className="fsc-risk-card__metric-value">{getMetricValue()}</span>

        <span className="fsc-risk-card__metric-unit">
          {category.metricUnit}
        </span>
      </p>

      <p className="fsc-risk-card__summary">{insight.description}</p>

      <div className="fsc-risk-card__meta">
        <div className="fsc-risk-card__meta-row">
          <span>
            <i className="ti ti-users" /> {affected.label}
          </span>
          <strong>{affected.value}</strong>
        </div>

        {category.id === "user-risk" && (
          <div className="fsc-risk-card__meta-row">
            <span>
              <i className="ti ti-world" /> Countries
            </span>
            <strong>{countries_count ?? 0}</strong>
          </div>
        )}

        <div className="fsc-risk-card__meta-row">
          <span>
            <i className="ti ti-alert-triangle" /> Risk level
          </span>

          <strong className={`fsc-risk-card__meta-value--${severity}`}>
            {risklevel || "UNKNOWN"}
          </strong>
        </div>

        <div className="fsc-risk-card__meta-row">
          <span>
            <i className="ti ti-clock" /> Last detected
          </span>

          <strong>{formatDate(lastdetected)}</strong>
        </div>

        <div className="fsc-risk-card__meta-row">
          <span>
            <i className="ti ti-database" /> Data source
          </span>

          <span className="fsc-risk-card__source-icons">
            {sourceList.length > 0 ? (
              sourceList.map((src, i) => (
                <img
                  key={i}
                  src={getDataSourceIcon(src)}
                  alt={src}
                  title={src}
                  className={`fsc-source-logo ${
                    (src || "").toLowerCase() === "onedrive"
                      ? "fsc-source-logo--onedrive"
                      : ""
                  }`}
                />
              ))
            ) : (
              <strong>N/A</strong>
            )}
          </span>
        </div>
      </div>

      <div className="fsc-risk-card__action-box">
        <p className="fsc-risk-card__action-label">Recommendation</p>

        <p className="fsc-risk-card__action-desc">{insight.recommendation}</p>

        <button
          className={`fsc-risk-card__action-btn fsc-risk-card__action-btn--${severity}`}
          onClick={(e) => {
            e.stopPropagation();
            onOpenAction(category.id);
          }}
        >
          <i className="ti ti-shield-check" />
          {getAction()}
        </button>
      </div>

      <button
        className="fsc-risk-card__link"
        onClick={(e) => {
          e.stopPropagation();
          onOpen(isStack ? null : category.id);
        }}
      >
        {isStack ? "View cards" : "View details"}
        <i className={`ti ${isStack ? "ti-layout-grid" : "ti-arrow-right"}`} />
      </button>
    </div>
  );
}

export function StatCard({ label, value, tone, icon, onClick }) {
  const clickable = typeof onClick === "function";

  return (
    <div
      className={`fsc-stat-badge fsc-stat-badge--${tone} ${
        clickable ? "fsc-stat-badge--clickable" : ""
      }`}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
    >
      {icon && (
        <span className="fsc-stat__icon">
          <i className={`ti ${icon}`} />
        </span>
      )}
      <span className="fsc-stat-badge__label">{label}</span>
      <span className="fsc-stat-badge__value">{value}</span>
    </div>
  );
}

export const DECK_TIMING = {
  CARD_DURATION: 520,
  CARD_STAGGER: 85,
  CARD_EASING: "cubic-bezier(0.22, 1, 0.36, 1)",
  DETAIL_FADE_MS: 240,
  AUTO_ROTATE_MS: 15000,
};

const FAN_X_NEAR = 180;
const FAN_X_FAR = 195;
const FAN_Y_NEAR = 12;
const FAN_Y_FAR = 30;
const FAN_ROTATE_STEP = 7;
const FAN_SCALE_NEAR = 0.92;
const FAN_SCALE_FAR = 0.78;

const STACK_PEEK_PX = 36;
const STACK_WIDTH_STEP = 22;
const STACK_BASE_WIDTH = 300;
const STACK_MIN_WIDTH = 190;
const CARD_HEIGHT = 580;

function getCardStyle(
  { fanOffset, fanAbsOffset, depth },
  isStackMode,
  maxDepth,
  length,
) {
  if (isStackMode) {
    const top = (maxDepth - depth) * STACK_PEEK_PX;
    const width = Math.max(
      STACK_MIN_WIDTH,
      STACK_BASE_WIDTH - depth * STACK_WIDTH_STEP,
    );

    return {
      transform: `translateX(-50%) translateX(0px) translateY(${top}px) rotate(0deg) scale(1)`,
      width,
      zIndex: 50 - depth,
      opacity: 1,
    };
  }

  const maxVisibleAbsOffset = length <= 4 ? 1 : 2;
  const isFarTier = fanAbsOffset === 2;

  const translateX = fanOffset * (fanAbsOffset === 2 ? FAN_X_FAR : FAN_X_NEAR);
  const translateY =
    fanAbsOffset === 2 ? FAN_Y_FAR : fanAbsOffset === 1 ? FAN_Y_NEAR : 0;
  const rotate = fanOffset * FAN_ROTATE_STEP;

  let scale = FAN_SCALE_FAR;
  if (fanAbsOffset === 0) scale = 1;
  else if (fanAbsOffset === 1) scale = FAN_SCALE_NEAR;

  const visible = fanAbsOffset <= maxVisibleAbsOffset;

  return {
    transform:
      length <= 4 && isFarTier
        ? `translateX(-50%) translateX(0px) translateY(0px) rotate(0deg) scale(${FAN_SCALE_NEAR})`
        : `translateX(-50%) translateX(${translateX}px) translateY(${translateY}px) rotate(${rotate}deg) scale(${scale})`,
    width: 300,
    zIndex: 20 - fanAbsOffset,
    opacity: visible ? 1 : 0,
  };
}

function useDeckLayout(orderedCategories, openRiskId) {
  const length = orderedCategories.length;
  const [fanIndex, setFanIndex] = useState(0);
  const lastOpenIndexRef = useRef(null);

  const openIndex = orderedCategories.findIndex((c) => c.id === openRiskId);

  useEffect(() => {
    if (openIndex >= 0) lastOpenIndexRef.current = openIndex;
  }, [openIndex]);

  const activeIndex = openIndex >= 0 ? openIndex : lastOpenIndexRef.current;
  const maxDepth = Math.max(length - 1, 0);

  const cards = orderedCategories.map((category, i) => {
    let fanOffset = i - fanIndex;
    if (fanOffset > length / 2) fanOffset -= length;
    else if (fanOffset < -length / 2) fanOffset += length;
    const fanAbsOffset = Math.abs(fanOffset);

    let depth = 0;
    let delayStep = 0;
    if (activeIndex != null && activeIndex >= 0) {
      depth = (i - activeIndex + length) % length;
      delayStep = openIndex >= 0 ? depth : maxDepth - depth;
    }

    return {
      category,
      index: i,
      fanOffset,
      fanAbsOffset,
      depth,
      delayMs: delayStep * DECK_TIMING.CARD_STAGGER,
    };
  });

  return { cards, fanIndex, setFanIndex, length, maxDepth };
}

export function RiskDeck({
  categories,
  openRiskId,
  onSelect,
  onOpenAction,
  detail,
}) {
  const dispatch = useDispatch();
  const isHovering = useRef(false);
  const summaries = useSelector((s) => s.dataControlReducer.cardSummary);
  const loader = useSelector((state) => state.dataControlReducer.loader);

  const orderedCategories = useMemo(
    () =>
      CARD_NAMES.map((name) => categories.find((c) => c.title === name)).filter(
        Boolean,
      ),
    [categories],
  );

  const { cards, fanIndex, setFanIndex, length, maxDepth } = useDeckLayout(
    orderedCategories,
    openRiskId,
  );
  const isStackMode = !!openRiskId;

  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    if (isStackMode || length <= 1) return;

    const timer = setInterval(() => {
      if (isHovering.current) return;
      setFanIndex((i) => {
        const next = (i + 1) % length;
        const peekName = orderedCategories[(next + 1) % length]?.title;
        if (peekName && !summaries[peekName]) {
          dispatch({
            type: GET_DATA_CONTROL_CARD_SUMMARY,
            payload: { cardName: peekName },
          });
          dispatch({
            type: GET_DATA_CONTROL_CARD_SUMMARY_COUNT,
            payload: { cardName: peekName },
          });
        }
        return next;
      });
    }, DECK_TIMING.AUTO_ROTATE_MS);

    return () => clearInterval(timer);
  }, [length, isStackMode]);

  useEffect(() => {
    if (isStackMode) {
      const totalCascadeMs =
        maxDepth * DECK_TIMING.CARD_STAGGER + DECK_TIMING.CARD_DURATION;
      const t = setTimeout(() => setShowDetail(true), totalCascadeMs);
      return () => clearTimeout(t);
    }
    setShowDetail(false);
  }, [isStackMode, openRiskId, maxDepth]);

  useEffect(() => {
    if (isStackMode && openRiskId === "public-sensitive") {
      dispatch({ type: GET_FILE_AUDIT_TRAIL });
    } else if (isStackMode && openRiskId === "user-risk") {
      dispatch({ type: GET_USER_AUDIT_TRAILS });
    } else if (isStackMode && openRiskId === "org-wide") {
      dispatch({ type: GET_DOMAIN_AUDIT_TRAILS });
    } else if (isStackMode) {
      dispatch({ type: GET_USER_LOCATION_AUDIT_TRAILS });
    }
  }, [isStackMode, openRiskId, dispatch]);

  useEffect(() => {
    if (isStackMode) return;
    const centerCategory = orderedCategories[fanIndex];
    if (centerCategory?.id) {
      dispatch({
        type: GET_RESTRICTIONS_APPLIED_COUNT,
        payload: { categoryId: centerCategory.id },
      });
    }
  }, [fanIndex, isStackMode, orderedCategories, dispatch]);

  useEffect(() => {
    if (!isStackMode || !openRiskId) return;
    dispatch({
      type: GET_RESTRICTIONS_APPLIED_COUNT,
      payload: { categoryId: openRiskId },
    });
  }, [isStackMode, openRiskId, dispatch]);

  const goPrev = () => setFanIndex((i) => (i - 1 + length) % length);
  const goNext = () => setFanIndex((i) => (i + 1) % length);

  const deckHeight = CARD_HEIGHT + maxDepth * STACK_PEEK_PX + 20;

  return (
    <div className="fsc-deck-row">
      <div
        className={`fsc-deck ${isStackMode ? "fsc-deck--stack" : "fsc-deck--fan"}`}
        onMouseEnter={() => (isHovering.current = true)}
        onMouseLeave={() => (isHovering.current = false)}
      >
        {!isStackMode && (
          <button
            className="fsc-fan-arrow fsc-fan-arrow--prev"
            onClick={goPrev}
            aria-label="Previous risk"
          >
            <i className="ti ti-chevron-left" />
          </button>
        )}

        <div
          className="fsc-deck__stage"
          style={{ height: deckHeight, zIndex: 1 }}
        >
          {cards.map(
            ({ category, index, fanOffset, fanAbsOffset, depth, delayMs }) => {
              const style = getCardStyle(
                { fanOffset, fanAbsOffset, depth },
                isStackMode,
                maxDepth,
                length,
              );
              const isHero = isStackMode ? depth === 0 : fanAbsOffset === 0;

              return (
                <div
                  key={category.id}
                  className={`fsc-deck__slot ${isHero ? "fsc-deck__slot--hero" : ""}`}
                  style={{
                    transform: style.transform,
                    width: style.width,
                    zIndex: style.zIndex,
                    opacity: style.opacity,
                    pointerEvents: style.opacity > 0.05 ? "auto" : "none",
                    transitionDuration: `${DECK_TIMING.CARD_DURATION}ms`,
                    transitionDelay: `${delayMs}ms`,
                    transitionTimingFunction: DECK_TIMING.CARD_EASING,
                  }}
                  onClick={() => {
                    if (isStackMode) {
                      if (loader) return;
                      if (isHero) {
                        const nextIndex = (index + 1) % length;
                        onSelect(orderedCategories[nextIndex].id);
                      } else {
                        onSelect(category.id);
                      }
                    } else if (!isHero) {
                      setFanIndex(index);
                    }
                  }}
                >
                  <CarouselCard
                    category={category}
                    cardData={summaries[category.title]}
                    onOpen={onSelect}
                    onOpenAction={onOpenAction}
                    isCenter={isHero}
                    isStack={isStackMode}
                    isNear={!isStackMode && fanAbsOffset === 1}
                  />
                </div>
              );
            },
          )}
        </div>

        {!isStackMode && (
          <button
            className="fsc-fan-arrow fsc-fan-arrow--next"
            onClick={goNext}
            aria-label="Next risk"
          >
            <i className="ti ti-chevron-right" />
          </button>
        )}

        {!isStackMode && (
          <div className="fsc-slider__dots">
            {orderedCategories.map((category, i) => (
              <button
                key={category.id}
                className={`fsc-slider__dot ${i === fanIndex ? "fsc-slider__dot--active" : ""}`}
                onClick={() => setFanIndex(i)}
                aria-label={`Go to ${category.title}`}
              />
            ))}
          </div>
        )}
      </div>

      {isStackMode && (
        <div
          className="fsc-detail-pane"
          style={{
            opacity: showDetail ? 1 : 0,
            pointerEvents: showDetail ? "auto" : "none",
            transitionDuration: showDetail
              ? `${DECK_TIMING.DETAIL_FADE_MS}ms`
              : "0ms",
          }}
        >
          {detail}
        </div>
      )}
    </div>
  );
}
